/**
 * Engine registry — the ONLY place engines are declared.
 * Adding a new engine = adding an entry here + a page + wiring the provider.
 *
 * Order matters: this is the order shown on Home and Sidebar.
 *
 * IMPORTANT: `status` controls navigation behavior. Only `live` engines
 * are fully interactive. `coming_soon` shows a placeholder.
 */

/**
 * `humanLabel` / `anticipationText`: el vocabulario que ve el Socio Fundador
 * (Sprint 11C, Iteración 2). `label` sigue siendo el nombre interno del
 * motor — no se muestra al usuario. Por ahora son las categorías genéricas;
 * el paquete de idioma por vertical (Iteración 3) sustituirá estos valores
 * por negocio cuando exista.
 *
 * @type {Array<{id: import('../contracts').EngineId, label: string, humanLabel: string, anticipationText: string, path: string, icon: string, status: import('../contracts').EngineStatus, question: string, tagline: string, group: "revenue"|"protect"|"insight"}>}
 */
export const ENGINES = [
  {
    id: "recovery",
    label: "Recovery",
    humanLabel: "Clientes que dejaron de comprarte",
    anticipationText: "",
    path: "/engines/recovery",
    icon: "activity",
    // Mismo mecanismo que Cross-Sell (Sprint 12): un despliegue puede
    // apagarlo con REACT_APP_RECOVERY_ENABLED=false sin tocar el motor —
    // ver la nota junto a RECOVERY_ENABLED en application/container.js.
    status: process.env.REACT_APP_RECOVERY_ENABLED === "false" ? "coming_soon" : "live",
    question: "¿Dónde está el dinero que perdí y puedo recuperar?",
    tagline: "Reactivación de cartera dormida",
    group: "revenue",
  },
  {
    id: "renewal",
    label: "Renewal",
    humanLabel: "Renovaciones próximas",
    anticipationText: "",
    path: "/engines/renewal",
    icon: "refresh-cw",
    status: "live",
    question: "¿Qué contratos vencen y cuánto ingreso está en riesgo?",
    tagline: "Renovaciones proactivas antes del vencimiento",
    group: "revenue",
  },
  {
    id: "cross_sell",
    label: "Cross-Sell",
    humanLabel: "Venta cruzada",
    anticipationText: "Estamos preparando esto con tu cartera — pronto lo vas a ver aquí.",
    path: "/engines/cross-sell",
    icon: "shuffle",
    // Sprint 12: mismo flag de activación que container.js — un solo lugar
    // decide "live" vs "coming_soon" por despliegue, nunca desincronizados.
    status: process.env.REACT_APP_CROSS_SELL_ENABLED === "true" ? "live" : "coming_soon",
    question: "¿A qué cliente puedo venderle algo que ya está listo para comprar?",
    tagline: "Venta cruzada sobre base activa",
    group: "revenue",
  },
  {
    id: "upsell",
    label: "Upsell",
    humanLabel: "Clientes con mayor potencial",
    anticipationText: "Estamos preparando esto con tu cartera — pronto lo vas a ver aquí.",
    path: "/engines/upsell",
    icon: "trending-up",
    status: "coming_soon",
    question: "¿Qué clientes están saturados y listos para pagar más?",
    tagline: "Expansión sobre clientes en uso intensivo",
    group: "revenue",
  },
  {
    id: "retention",
    label: "Retention",
    humanLabel: "Seguimientos pendientes",
    anticipationText: "Estamos preparando esto con tu cartera — pronto lo vas a ver aquí.",
    path: "/engines/retention",
    icon: "shield-check",
    status: "coming_soon",
    question: "¿Qué clientes están por irse y aún puedo salvar?",
    tagline: "Prevención de churn antes de que ocurra",
    group: "protect",
  },
  {
    id: "referral",
    label: "Referral",
    humanLabel: "Clientes que te recomiendan",
    anticipationText: "Estamos preparando esto con tu cartera — pronto lo vas a ver aquí.",
    path: "/engines/referral",
    icon: "share-2",
    status: "coming_soon",
    question: "¿Cuál de mis clientes felices puede traerme el siguiente cliente?",
    tagline: "Activación sistemática de la red de clientes",
    group: "revenue",
  },
  {
    id: "prospecting",
    label: "Prospecting",
    humanLabel: "Clientes nuevos",
    anticipationText: "Estamos preparando esto con tu cartera — pronto lo vas a ver aquí.",
    path: "/engines/prospecting",
    icon: "target",
    status: "coming_soon",
    question: "¿Qué prospectos se parecen a mis mejores clientes?",
    tagline: "Adquisición dirigida por ICP empírico",
    group: "revenue",
  },
];

/** Engines used to build Home cards. Excludes purely diagnostic engines. */
export const REVENUE_ENGINES = ENGINES.filter(
  (e) => e.group === "revenue" || e.group === "protect"
);

/** @param {import('../contracts').EngineId} id */
export const getEngine = (id) => ENGINES.find((e) => e.id === id);
